import React from 'react';

import { ButtonSize, ButtonType } from './constants';

export interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  disabled?: boolean;
  doSubmit?: boolean;
  buttonSize?: ButtonSize;
  buttonType?: ButtonType;
}

const outlinedButtonClasses = 'focus:text-sapphire focus:border-sapphire-300 '
  + 'hover:text-sapphire hover:border-sapphire-300 active:text-sapphire-700 active:border-sapphire-900 '
  + 'disabled:!border-neutral-200 disabled:!text-neutral-300 disabled:pointer-events-none';

const mapTypeToClasses = {
  [ButtonType.facebook]:
    'cursor-pointer text-white py-2 rounded-full font-semibold bg-facebookBlue '
      + 'focus:bg-facebookBlue-300 hover:bg-facebookBlue-300 active:bg-facebookBlue-300 '
      + 'disabled:text-white disabled:bg-neutral-500 disabled:border-neutral-500 disabled:pointer-events-none',
  [ButtonType.primary]:
    'cursor-pointer text-white py-2 rounded-full font-semibold bg-sapphire '
      + 'focus:bg-sapphire-700 hover:bg-sapphire-700 active:bg-sapphire-100 '
      + 'disabled:text-white disabled:bg-neutral-500 disabled:border-neutral-500 disabled:pointer-events-none',
  [ButtonType.rounded]:
    'cursor-pointer text-black border-black border-solid flex items-center justify-center rounded-full bg-transparent border '
      + outlinedButtonClasses,
  [ButtonType.secondary]:
    'cursor-pointer text-sapphire border-sapphire py-[0.4375rem] rounded-full bg-transparent border '
      + outlinedButtonClasses,
  [ButtonType.tertiary]:
    'cursor-pointer text-sapphire py-1.5 rounded-full min-w-fit text-sm '
      + 'focus:text-sapphire-700 hover:text-sapphire-700 active:text-sapphire-100 '
      + 'disabled:text-black disabled:pointer-events-none',
  [ButtonType.textLink]:
    'cursor-pointer text-black py-2 rounded-full font-semibold '
      + 'focus:bg-sapphire-700 focus:text-white hover:bg-sapphire-700 hover:text-white active:bg-sapphire-100 '
      + 'disabled:text-white disabled:bg-neutral-500 disabled:border-neutral-500 disabled:pointer-events-none',
};

const mapSizeToClasses = {
  [ButtonSize.large]: 'px-12 min-w-[10rem]',
  [ButtonSize.medium]: 'px-5 min-w-[8rem]',
  [ButtonSize.mediumSquare]: 'w-[38px] h-[38px]',
  [ButtonSize.smallMedium]: 'px-3 md:px-5 min-w-[5rem] md:min-w-[8rem]',
  [ButtonSize.small]: 'px-3',
  [ButtonSize.wide]: 'px-5 min-w-[20rem] md:min-w-[22rem]',
};

const Button = (props: Props): JSX.Element => {
  const { children, className, buttonSize, buttonType, onClick, doSubmit, ...rest } = props;

  const getSizeClasses = (): string => {
    // default to medium except in a few cases
    // it would be REALLY nice to not have the overrides eventually
    let size = buttonSize;
    if (buttonType !== ButtonType.tertiary && !size && buttonType) {
      size = ButtonSize.medium;
    }

    return size ? ` ${mapSizeToClasses[size]}` : '';
  }

  // TODO: wrangle all buttons into needing a type and make it a required prop
  const typeClasses = buttonType ? ` ${mapTypeToClasses[buttonType]}` : '';
  const sizeClasses = getSizeClasses();

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    if (onClick) {
      onClick(e);
    }
  };

  return (
    // order matters for the classes
    <button
      type={doSubmit ? 'submit' : 'button'}
      className={`transition-all duration-500 disabled:opacity-60${typeClasses}${sizeClasses}${className ? ` ${className}` : ''}`}
      onClick={handleClick}
      {...rest}
    >
      {children}
    </button>
  );
};

export { Button, ButtonSize, ButtonType, outlinedButtonClasses };
